import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Ship, ArrowLeft, Rocket, Camera, PlayCircle, Terminal, PenTool, Tag, Plug,
  Users, CreditCard, LifeBuoy, ChevronRight, Keyboard, Palette, Map, Smile, Target, ShieldCheck,
  Sparkles, Copy, Check, Hash, Bell, MessageSquareText,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOPICS = [
  { id: "getting-started", label: "Getting started", icon: Rocket },
  { id: "capture-types", label: "Capture types", icon: Camera },
  { id: "shortcut", label: "Keyboard shortcut", icon: Keyboard },
  { id: "ratings", label: "Sentiment ratings", icon: Smile },
  { id: "session-replay", label: "Session replay", icon: PlayCircle },
  { id: "privacy", label: "Privacy & PII redaction", icon: ShieldCheck },
  { id: "diagnostics", label: "Console, network & env", icon: Terminal },
  { id: "annotations", label: "Annotated screenshots", icon: PenTool },
  { id: "roadmap", label: "Roadmap & upvotes", icon: Map },
  { id: "targeting", label: "Audience targeting", icon: Target },
  { id: "triage", label: "Triage & filters", icon: Tag },
  { id: "numbers-activity", label: "Numbers & activity log", icon: Hash },
  { id: "tester-updates", label: "Tester updates & replies", icon: Bell },
  { id: "guided", label: "Guided submissions", icon: MessageSquareText },
  { id: "branding", label: "Custom branding", icon: Palette },
  { id: "integrations", label: "Jira integration", icon: Plug },
  { id: "team", label: "Team & roles", icon: Users },
  { id: "billing", label: "Plans & billing", icon: CreditCard },
  { id: "support", label: "Getting support", icon: LifeBuoy },
];

const snippet = `<script src="${BACKEND_URL}/widget.js"
        data-project-key="pk_your_key"></script>`;

function H({ children }) { return <h2 className="font-display text-3xl font-700 tracking-tight mb-3">{children}</h2>; }
function P({ children }) { return <p className="text-ink/75 font-600 leading-relaxed mb-4">{children}</p>; }
function Sub({ children }) { return <h3 className="font-display text-lg font-700 mt-7 mb-2">{children}</h3>; }
function Li({ children }) { return <li className="flex gap-2 text-ink/75 font-600"><ChevronRight className="h-4 w-4 text-orange shrink-0 mt-1" /><span>{children}</span></li>; }
function Code({ children }) { return <pre className="bg-ink text-sunny text-xs p-4 rounded-2xl overflow-x-auto font-mono border-2 border-ink shadow-nb-sm my-4 whitespace-pre-wrap">{children}</pre>; }
function Note({ children }) { return <div className="bg-sunny/50 border-2 border-ink rounded-2xl p-4 text-sm font-700 my-4">{children}</div>; }
function Kbd({ children }) { return <kbd className="inline-block font-mono text-xs bg-sunny border-2 border-ink rounded-md px-1.5 py-0.5 shadow-nb-sm mx-0.5">{children}</kbd>; }

const AI_PROMPT = `Install the Tugboat feedback widget into my web app.

1. Add this script tag right before </body> (works with any framework — React, Vue, Angular, Svelte, Next.js or plain HTML; no npm install needed):
   <script src="${BACKEND_URL}/widget.js" data-project-key="pk_your_key"></script>
   Replace pk_your_key with my project key from the Tugboat dashboard (Projects → Settings).

Optional configuration:
- Keyboard shortcut: add data-hotkey="mod+shift+k" to the script tag (mod = Ctrl on Windows/Linux, ⌘ on Mac). Use data-hotkey="none" to disable.
- Manual trigger (no floating button): set the project to "manual" mode in Settings, then call Tugboat.open() from my own button/menu item.
- Audience targeting (for changelog & roadmap posts): identify the signed-in user with
  Tugboat.identify({ plan: "pro", role: "admin", segments: ["beta"] });
  or set window.tugboatUser = { plan, role, segments } before the script loads.
- Privacy: session replays already mask all inputs and auto-redact emails/phones/card numbers. Mark any sensitive region in my markup with data-tugboat-block (never recorded) or data-tugboat-mask (text masked).

Result: a floating "Feedback" button appears. Testers can send screenshots (with annotation), screen recordings with voice, voice notes and text — each with console/network logs, environment metadata and a session replay attached automatically.`;

function AiInstall() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(AI_PROMPT); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
  };
  return (
    <div className="bg-ink text-cream border-2 border-ink rounded-2xl p-5 my-5 shadow-nb-sm" data-testid="ai-install">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-orange border-2 border-cream rounded-xl flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-white" /></div>
          <div>
            <h3 className="font-display font-700 text-lg">Install with AI</h3>
            <p className="text-cream/70 font-600 text-sm max-w-md">Using Cursor, Copilot, Claude or ChatGPT? Copy this prompt, paste it into your AI assistant, and it'll wire Tugboat into your app for you.</p>
          </div>
        </div>
        <button onClick={copy} data-testid="ai-install-copy"
          className="nb-btn bg-orange text-white px-4 py-2.5 text-sm shrink-0 inline-flex items-center gap-2">
          {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy prompt for AI</>}
        </button>
      </div>
      <pre className="bg-black/40 text-cream/90 text-xs p-4 rounded-xl overflow-x-auto font-mono border-2 border-cream/20 mt-4 whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid="ai-install-prompt">{AI_PROMPT}</pre>
      <p className="text-cream/60 font-600 text-xs mt-3">Our docs are also machine-readable at <code className="font-mono text-sunny">/llms.txt</code> and <code className="font-mono text-sunny">/llms-full.txt</code> — point your AI agent straight at them.</p>
    </div>
  );
}

const CONTENT = {
  "getting-started": (
    <>
      <H>Getting started</H>
      <P>Tugboat is a floating feedback button you embed in your web app. Testers tap it to send screenshots, screen recordings, voice notes and text — with technical context attached automatically. Here's the 2-minute setup. <b>These docs are AI-friendly</b> — you can hand the setup to your favorite AI assistant with one copy-paste.</P>
      <AiInstall />
      <Sub>1. Create a project</Sub>
      <P>In your dashboard, go to <b>Projects → New project</b>. Each project gets a unique <code className="font-mono bg-sand border-2 border-ink px-1.5 py-0.5 rounded text-xs">project_key</code> (looks like <code className="font-mono text-xs">pk_…</code>). Use one project per app or client deployment.</P>
      <Sub>2. Embed the widget</Sub>
      <P>Drop this one line before your closing <code className="font-mono text-xs">&lt;/body&gt;</code> tag:</P>
      <Code>{snippet}</Code>
      <P>It works with React, Vue, Angular, Svelte and plain HTML — any web app, plus mobile web & PWAs. No npm install required.</P>
      <Note>Lightweight by design: the core widget is <b>~22KB gzipped</b> and renders entirely inside its own <b>Shadow DOM</b>, so its styles never leak into your app (and your CSS can't break it). Session replay (rrweb, ~119KB) is only downloaded for projects on a plan with replay enabled — if you don't use it, that code never loads.</Note>
      <Sub>3. Collect feedback</Sub>
      <P>A draggable tug appears bottom-right. Testers click it, pick a capture type, and their feedback sails straight to your dashboard, tagged by project. They can also press <Kbd>Ctrl</Kbd>/<Kbd>⌘</Kbd> <Kbd>Shift</Kbd> <Kbd>K</Kbd> to open it instantly — see <b>Keyboard shortcut</b>.</P>
      <Note>Want to preview it first? Open any project and use the live <b>Preview</b> route to see the widget running in a sandbox.</Note>
    </>
  ),
  "shortcut": (
    <>
      <H>Keyboard shortcut</H>
      <P>Testers (and your team) can open Tugboat instantly without hunting for the floating button — handy when a bug strikes mid-typing and you don't want to reach for the mouse.</P>
      <ul className="space-y-2.5 my-4">
        <Li>Press <Kbd>Ctrl</Kbd> <Kbd>Shift</Kbd> <Kbd>K</Kbd> on Windows/Linux, or <Kbd>⌘</Kbd> <Kbd>⇧</Kbd> <Kbd>K</Kbd> on Mac, to open the panel from anywhere on the page.</Li>
        <Li>It works even while the cursor is focused in a form field.</Li>
        <Li>Press <Kbd>Esc</Kbd> to close the panel.</Li>
      </ul>
      <Sub>Customize the shortcut</Sub>
      <P>No code required — open a project's <b>Settings → Keyboard shortcut</b>, toggle it on or off, and click <b>Record shortcut</b> to press your own combo. It saves to that project and applies everywhere the widget is embedded (and in the live Preview).</P>
      <Note>Pick a combo that won't clash with your own app's shortcuts. Ctrl and ⌘ are interchangeable, so the same setting works on both Windows and Mac.</Note>
      <Sub>Advanced: set it in the embed code</Sub>
      <P>Prefer to pin it per-embed? Add a <code className="font-mono text-xs">data-hotkey</code> attribute to the script tag — it overrides the dashboard setting. Use <code className="font-mono text-xs">mod</code> for Ctrl/⌘, combined with <code className="font-mono text-xs">shift</code>, <code className="font-mono text-xs">alt</code> and a key:</P>
      <Code>{`<script src="${BACKEND_URL}/widget.js"
        data-project-key="pk_your_key"
        data-hotkey="ctrl+shift+f"></script>`}</Code>
      <P>To turn the shortcut off entirely, set <code className="font-mono text-xs">data-hotkey="none"</code> (or toggle it off in Settings). The default is <b>Ctrl/⌘ + Shift + K</b>.</P>
    </>
  ),
  "ratings": (
    <>
      <H>Sentiment ratings</H>
      <P>Every feedback form includes an optional <b>emoji sentiment scale</b> — 😠 🙁 😐 🙂 😍 — so testers can tell you how they feel in one tap, not just what they typed. It turns raw notes into a lightweight, NPS-style signal you can scan and filter at a glance.</P>
      <ul className="space-y-2.5 my-4">
        <Li>Shown on the <b>Text note</b> form and after any screenshot / recording / voice capture.</Li>
        <Li>Completely optional — testers can send feedback without picking a face.</Li>
        <Li>The chosen sentiment appears on the feedback row and detail in your dashboard.</Li>
      </ul>
      <Note>No setup required — sentiment ratings are on for every project and plan.</Note>
    </>
  ),
  "roadmap": (
    <>
      <H>Roadmap & upvotes</H>
      <P>Close the loop with your testers. The widget includes a <b>Roadmap</b> panel that shows what you're <b>Planned</b>, <b>In progress</b> and <b>Released</b> — and lets users <b>upvote</b> what they want most, so you build the right things next.</P>
      <Sub>Managing your roadmap</Sub>
      <P>Open a project and go to the <b>Roadmap</b> tab. Add items with a title, optional description and a status. Drag items between statuses using each card's dropdown, and see live vote totals per item.</P>
      <Sub>How upvoting works</Sub>
      <ul className="space-y-2.5 my-4">
        <Li><b>Anonymous & frictionless</b> — testers vote with a single tap, no login or email required.</Li>
        <Li><b>One vote per browser</b> — votes are de-duplicated per device, and tapping again removes the vote.</Li>
      </ul>
      <Note>The Roadmap is available on <b>every plan</b>, including Solo. Prefer to hide it? It follows your white-label settings and can be scoped to specific audiences (see <b>Audience targeting</b>).</Note>
    </>
  ),
  "targeting": (
    <>
      <H>Audience targeting</H>
      <P>Announce beta features to the right people without confusing everyone. Both <b>Changelog</b> ("What's new") and <b>Roadmap</b> posts can be targeted to a cohort based on the user context you pass to the widget.</P>
      <Sub>1. Tell Tugboat who the user is</Sub>
      <P>Pass a <code className="font-mono text-xs">user</code> object with any of <code className="font-mono text-xs">plan</code>, <code className="font-mono text-xs">role</code> and <code className="font-mono text-xs">segments</code>. Set it before the widget loads, or call <code className="font-mono text-xs">Tugboat.identify(...)</code> once you know who's signed in:</P>
      <Code>{`<!-- Option A: set it before the script tag -->
<script>
  window.tugboatUser = { plan: "pro", role: "admin", segments: ["beta"] };
</script>
<script src="${BACKEND_URL}/widget.js" data-project-key="pk_your_key"></script>

<!-- Option B: identify after login -->
<script>Tugboat.identify({ plan: "pro", role: "admin", segments: ["beta"] });</script>`}</Code>
      <Sub>2. Target a post</Sub>
      <P>When creating a changelog or roadmap item, fill in the optional <b>Audience</b> fields (Plans, Roles, Segments — comma-separated). Leave them blank to show the post to <b>everyone</b>.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Plans / Roles</b> — the user must match one of the listed values.</Li>
        <Li><b>Segments</b> — the user must hold at least one of the listed segments.</Li>
        <Li>Fields combine with AND — e.g. plan <code className="font-mono text-xs">pro</code> <i>and</i> segment <code className="font-mono text-xs">beta</code>.</Li>
      </ul>
      <Note>No user context passed? Targeted posts stay hidden and only untargeted (public) posts show — a safe default.</Note>
    </>
  ),
  "capture-types": (
    <>
      <H>Capture types</H>
      <P>Testers can submit feedback four ways — each one attaches environment metadata and console/network logs automatically.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Screenshot</b> — captures the screen and opens an annotation editor so the tester can draw a box/arrow directly on the issue, and attach an optional <b>voice note</b> explaining it. The widget <b>hides itself while capturing</b>, so it never shows up in the shot.</Li>
        <Li><b>Screen recording</b> — records the screen <b>with microphone narration</b> (the tester's voice is mixed in, so you hear them talk through the bug) up to your plan's limit. The browser's own share dialog lets the tester pick the <b>current tab, another tab, a window, or the whole screen</b>.</Li>
        <Li><b>Voice note</b> — a quick audio explanation in the tester's own words.</Li>
        <Li><b>Text note</b> — written context, always attached.</Li>
      </ul>
      <Sub>Recording limits by plan</Sub>
      <ul className="space-y-2.5">
        <Li>Solo: 30 seconds · Team: 3 minutes · Business: 10 minutes · Enterprise: custom</Li>
      </ul>
    </>
  ),
  "session-replay": (
    <>
      <H>Session replay</H>
      <P>Session replay is a lightweight DVR for the page. Using <b>rrweb</b>, the widget continuously records the DOM and interactions, keeping roughly the <b>last 30 seconds</b> in memory. When a tester submits feedback, that buffer is attached — so you can replay exactly what they did leading up to the bug.</P>
      <Sub>How to watch a replay</Sub>
      <P>Open a feedback item in the dashboard. If a replay was captured, you'll see a <b>"▶ Replay session"</b> button — click it to load the rrweb player with play/pause and speed controls.</P>
      <Sub>Privacy</Sub>
      <P>All input fields are masked by default, so passwords and typed values are never recorded. On top of that, Tugboat can <b>auto-redact PII</b> (emails, phone numbers and card-like numbers) from all recorded text, and you can block or mask any region of the page with a single attribute — see <b>Privacy & PII redaction</b>. Replays are stored with your feedback and only visible to your team.</P>
      <Note>Session replay is available on <b>Team, Business and Enterprise</b> plans. Solo plans keep screenshots, recordings, console & network logs.</Note>
    </>
  ),
  "privacy": (
    <>
      <H>Privacy & PII redaction</H>
      <P>Tugboat is built to capture what developers need without hoovering up your users' personal data. Protection applies to <b>session replays</b>, where rendered page text would otherwise be recorded.</P>
      <Sub>What's protected automatically</Sub>
      <ul className="space-y-2.5 my-4">
        <Li><b>All form inputs are masked</b> — passwords and anything typed into fields are never captured.</Li>
        <Li><b>Auto PII redaction (on by default)</b> — emails, phone numbers and card-like number sequences in rendered text are replaced with <code className="font-mono text-xs">[redacted]</code> before the replay ever leaves the browser.</Li>
      </ul>
      <P>Toggle auto-redaction per project under <b>Settings → Privacy & PII redaction</b>. It's on by default for every plan.</P>
      <Sub>Block or mask specific regions</Sub>
      <P>Need finer control? Add one attribute to any element in your own markup:</P>
      <ul className="space-y-2.5 my-4">
        <Li><code className="font-mono text-xs">data-tugboat-block</code> — the element and its contents are <b>not recorded at all</b> (great for a billing panel or an SSN field). You can also use the class <code className="font-mono text-xs">tugboat-block</code>.</Li>
        <Li><code className="font-mono text-xs">data-tugboat-mask</code> — the element's <b>text is masked</b> but its layout is preserved. Class equivalent: <code className="font-mono text-xs">tugboat-mask</code>.</Li>
      </ul>
      <Code>{`<!-- never recorded -->
<section data-tugboat-block> ...sensitive account details... </section>

<!-- shape kept, text masked -->
<p data-tugboat-mask>Order total: $4,208.55</p>`}</Code>
      <Note>Redaction happens <b>client-side</b>, before any data is sent. Combined with Shadow DOM isolation, the widget stays a good citizen inside even highly regulated apps.</Note>
    </>
  ),
  "diagnostics": (
    <>
      <H>Console, network & environment</H>
      <P>Every submission carries the technical context developers need to reproduce a bug — captured automatically, with zero tester effort.</P>
      <Sub>Console logs</Sub>
      <P>The widget buffers the most recent <b>console errors and warnings</b>, plus uncaught exceptions and unhandled promise rejections. They appear in a collapsible "Console & network logs" panel on the feedback detail.</P>
      <Sub>Network errors</Sub>
      <P>Failed requests (HTTP status ≥ 400 or network errors) from both <code className="font-mono text-xs">fetch</code> and <code className="font-mono text-xs">XHR</code> are recorded with method, URL, status and duration.</P>
      <Sub>Environment metadata</Sub>
      <P>OS, browser & version, viewport, screen size, device pixel ratio, language, timezone and online status are auto-detected and shown in an Environment block.</P>
      <Sub>Device &amp; platform</Sub>
      <P>Every submission is tagged with the <b>device it came from</b> — the actual OS is read from the capture, so you see <b>Android</b> or <b>iOS</b> (phone icon) for mobile, and <b>Windows</b>, <b>macOS</b> or <b>Linux</b> (desktop icon) for computers. Submissions sent from a mobile SDK show <b>iOS app</b> / <b>Android app</b>. The badge appears on the feedback list, the detail view and next to the <b>Session replay</b>, so you instantly know the platform a bug was reported on.</P>
      <Note>Reports that include a console error or failed request are flagged with a 🔥 <b>Errors</b> badge — use the signal filter to surface them first.</Note>
    </>
  ),
  "annotations": (
    <>
      <H>Annotated screenshots</H>
      <P>After a tester captures a screenshot, an in-widget editor opens right away so they can mark up the problem before sending — no confusing steps in between. The widget hides itself during capture, so it's never in the image.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Box</b> — draw a rectangle around the issue.</Li>
        <Li><b>Arrow</b> — point directly at what's broken.</Li>
        <Li><b>Pen</b> — freehand highlight anything.</Li>
        <Li>Four colors, plus <b>Undo</b> and <b>Clear</b>.</Li>
      </ul>
      <P>Annotations are flattened onto the full-resolution image, so what you see in the dashboard is exactly what the tester drew.</P>
    </>
  ),
  "triage": (
    <>
      <H>Triage & filters</H>
      <P>Keep your feedback inbox organized and surface the highest-signal reports.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Status</b> — move items through New → In progress → Resolved. You can change status right from the list row or the detail view — even on archived items.</Li>
        <Li><b>Views</b> — switch between <b>Inbox</b> (active items), <b>Archived</b>, or <b>All</b>. This view is independent of status, so you can, e.g., see all <i>Archived</i> items that are still marked <i>New</i>.</Li>
        <Li><b>Tags</b> — add colored, free-form tags and filter by them.</Li>
        <Li><b>Archive</b> — clear items from the inbox without deleting; restore anytime. Archived items keep their status and stay filterable.</Li>
        <Li><b>Signal filter</b> — show only feedback that has errors (🔥) or a session replay (▶).</Li>
        <Li><b>Subjects</b> — let testers pick a topic when submitting, then filter by it.</Li>
      </ul>
      <Note>The view (Inbox/Archived/All), status, signal and subject filters all stack — combine them to slice your feedback any way you need.</Note>
    </>
  ),
  "numbers-activity": (
    <>
      <H>Numbers &amp; activity log</H>
      <P>Every submission gets a <b>canonical number</b> (e.g. <code className="font-mono text-xs">#42</code>), unique and sequential per project — so your team can reference bugs like a real tracker ("can you look at #42?"). Numbers show on the feedback list, the detail view and the activity report.</P>
      <Sub>Full audit trail</Sub>
      <P>Open any submission to see its <b>Activity &amp; replies</b> timeline. Tugboat records who changed what, and when:</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Status &amp; priority changes</b> — every move (New → In progress → Resolved) is logged with the teammate's name.</Li>
        <Li><b>Tags, archive/restore, Jira links</b> — all tracked automatically.</Li>
        <Li><b>Internal notes</b> — leave team-only comments on a submission ("repro'd on staging, assigning to Sam"). Only your team sees these.</Li>
      </ul>
      <Sub>Project Activity tab</Sub>
      <P>Each project has an <b>Activity</b> tab — a single feed of every change across all submissions, by every teammate. Filter by date range and change type. Every entry shows <b>which submission</b> it belongs to (its number and subject/note), so a line like "Archived" or "Status → Resolved" always has context — and internal notes, public replies and tester replies show their full text inline.</P>
      <Sub>Export a PDF report</Sub>
      <P>Managers can hit <b>Export PDF</b> in the Activity tab to download a branded audit report of all changes over any date range — perfect for standups, client reporting or compliance. The report includes a dedicated <b>Submission</b> column (subject or note snippet) plus the full text of every note and reply, so it reads as a complete history, not just a list of actions.</P>
    </>
  ),
  "tester-updates": (
    <>
      <H>Tester updates &amp; two-way replies</H>
      <P>Close the loop with the people who report bugs. Every tester gets a private status link the moment they submit — and those who opt in also get email updates and a real back-and-forth conversation.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Instant status link</b> — right after submitting, the widget's "Thank you" screen shows the tester a <b>private link to their report</b> (with a one-tap Copy). They can bookmark it to follow progress — no email or account required.</Li>
        <Li><b>Opt-in email updates</b> — when a tester leaves an email, they can tick <i>"Email me when there's an update"</i> to also be emailed on replies and status changes.</Li>
        <Li><b>Public replies vs internal notes</b> — in the submission's timeline you choose: an <b>internal note</b> (team-only) or a <b>public reply</b> that's shared with the tester. (If a report has no contact info, only internal notes are available.)</Li>
        <Li><b>Public status page</b> — the link opens a tester-facing page showing the current status and the public conversation (internal notes are never shown). The tester can <b>reply back</b> with more detail, which lands right in your dashboard timeline.</Li>
      </ul>
      <Sub>Auto-publish resolved fixes</Sub>
      <P>Turn on <b>Auto-publish resolved fixes</b> (on the Changelog tab) and whenever you mark a submission <b>Resolved</b>, Tugboat automatically posts a "Fixed:" entry to your widget's <b>What's New</b> feed — using your public reply if there is one. Testers across the board see you're shipping, which is great for beta engagement.</P>
      <Sub>The "What's New" notification dot</Sub>
      <P>When you publish new changelog entries, testers see a small <b>red dot</b> on the feedback button — a nudge that there are updates they haven't read. Opening the widget reveals a <b>"What's new"</b> button (also marked with the dot); once they view it, the dot clears for that device. The dot and button only appear when <b>What's New is shown in the widget</b> and isn't hidden by your white-label settings.</P>
      <Note>Turning on <b>Auto-publish resolved fixes</b> automatically shows the "What's new" panel in the widget — you can't publish into a hidden panel, so the two settings stay in sync.</Note>
      <Note>Emails are sent via your Resend integration — make sure your sending domain is verified so updates land in the inbox, not spam.</Note>
    </>
  ),
  "guided": (
    <>
      <H>Guided submissions</H>
      <P>Non-technical testers often send vague reports ("it's broken"). Guided submissions coach them into giving you the context you actually need — turn it on per project.</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Guided prompts</b> — instead of one blank note box, the widget shows short questions like <i>"What were you trying to do?"</i>, <i>"What did you expect?"</i> and <i>"What actually happened?"</i>. Answers are assembled into one clean, structured report.</Li>
        <Li><b>Custom prompts</b> — edit, add or remove the questions to fit your product (up to six).</Li>
        <Li><b>Persona dropdown</b> — optionally add an <i>"I'm reporting as…"</i> selector (e.g. End user, Admin, Developer, QA) so you know who's talking.</Li>
        <Li><b>Off by default</b> — enable it in <b>Settings → Guided submissions</b> whenever you want; the standard free-text note is used otherwise.</Li>
      </ul>
    </>
  ),
  "branding": (
    <>
      <H>Custom branding</H>
      <P>Make the widget feel like a native part of your product. Custom branding is available on the <b>Business</b> plan (or via a per-org feature unlock). Configure it per project in <b>Settings → Custom branding</b>.</P>
      <Sub>The floating button</Sub>
      <ul className="space-y-2.5 my-4">
        <Li><b>Accent color</b> — recolors the button and the widget's primary actions.</Li>
        <Li><b>Button text</b> — the label shown on the pill button (e.g. "Feedback", "Report a bug").</Li>
        <Li><b>Button shape</b> — Pill (icon + text), Circle or Rounded square (icon only).</Li>
        <Li><b>Default corner</b> — choose which corner the button first appears in (bottom-right, bottom-left, top-right or top-left). Testers can still drag it anywhere.</Li>
        <Li><b>Show a close (×) button</b> — optionally let testers dismiss the widget: a small × appears on the button, and clicking it hides the launcher (per browser) until they tap a small "Feedback" tab to bring it back.</Li>
        <Li><b>Button icon</b> — pick from a set of preset icons: ship, chat, bug, lightbulb, megaphone, heart, flag, star, lifebuoy or sparkles. A live preview updates as you choose.</Li>
        <Li><b>Logo</b> — upload your own logo to use instead of a preset icon (your logo always takes precedence over the icon).</Li>
        <Li><b>Match capture buttons to brand</b> — toggle this to tint all four capture tiles (screenshot, record, voice, note) from your accent color for a fully cohesive look, instead of the default multicolor tiles.</Li>
      </ul>
      <Sub>White-label the widget text</Sub>
      <P>Remove every trace of "Tugboat": set a <b>brand name</b>, customize the panel <b>headline</b>, the capture-type labels, the send / sending / thank-you / consent text, and optionally <b>hide the "What's new" panel</b> for a fully white-labeled experience.</P>
      <Note>Tip: pick a preset <b>button icon</b> if you don't have a square logo handy — it's the fastest way to match the widget to your product's icon style.</Note>
    </>
  ),
  "integrations": (
    <>
      <H>Jira integration</H>
      <P>Push a tester's report straight into Jira as an issue — with the screenshot, video or voice note attached.</P>
      <Sub>Connect Jira</Sub>
      <ol className="space-y-2.5 my-4 list-none">
        <Li>Open a project → <b>Settings → Jira Cloud</b>.</Li>
        <Li>Enter your Jira site URL, Atlassian email, an API token, the project key and issue type.</Li>
        <Li>Your token is encrypted before storage.</Li>
      </ol>
      <Sub>Two-way sync</Sub>
      <P>Click <b>Send to Jira</b> on any feedback to create a linked issue. When that issue is marked done in Jira, Tugboat auto-resolves the feedback (manual "Sync from Jira" button + a background poller every 5 minutes).</P>
      <Note>Linear, GitHub, Asana, Trello and ClickUp are on the roadmap.</Note>
    </>
  ),
  "team": (
    <>
      <H>Team & roles</H>
      <P>Every account belongs to an organization. Invite teammates by email from the <b>Team</b> page — they join your org automatically when they register.</P>
      <Sub>Roles</Sub>
      <ul className="space-y-2.5 my-4">
        <Li><b>Owner</b> — full control, including billing.</Li>
        <Li><b>Admin</b> — manage team, projects and billing.</Li>
        <Li><b>Member</b> — create projects and triage feedback.</Li>
        <Li><b>Viewer</b> — read-only access.</Li>
      </ul>
      <P>Members and Viewers can be restricted to specific projects. Roles & per-project access are available on Business and Enterprise plans.</P>
    </>
  ),
  "billing": (
    <>
      <H>Plans & billing</H>
      <P>Manage your subscription from the <b>Billing</b> page (owners & admins).</P>
      <ul className="space-y-2.5 my-4">
        <Li><b>Solo</b> — free forever: 1 project, 100 feedback/mo, 7-day history.</Li>
        <Li><b>Team ($39/mo)</b> — unlimited projects, 5 teammates, integrations, session replay, unlimited history.</Li>
        <Li><b>Business ($149/mo)</b> — everything in Team plus white-label, roles & priority support.</Li>
        <Li><b>Enterprise</b> — custom: SSO, audit logs, SLA & dedicated support.</Li>
      </ul>
      <Sub>Upgrading & downgrading</Sub>
      <P>Changing plans mid-cycle is prorated automatically — you're only charged the difference today, and your next invoice reflects the new plan. You'll get an email confirmation for every change.</P>
    </>
  ),
  "support": (
    <>
      <H>Getting support</H>
      <P>Need a hand? We're quick to respond.</P>
      <ul className="space-y-2.5 my-4">
        <Li>From inside the app, open <b>Support</b> in the sidebar and send us a message (billing, technical, account or general).</Li>
        <Li>Prefer email? Reach us at <a href="mailto:hello@tugboatapp.com" className="text-orange font-800 underline">hello@tugboatapp.com</a>.</Li>
        <Li>We reply to every ticket by email.</Li>
      </ul>
    </>
  ),
};

export default function Docs() {
  const { user } = useAuth();
  const [active, setActive] = useState("getting-started");

  return (
    <div className="min-h-screen bg-cream" data-testid="docs-page">
      <header className="border-b-2 border-ink bg-sunny">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-orange border-2 border-ink rounded-xl flex items-center justify-center shadow-nb-sm">
              <Ship className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-700 text-xl tracking-tight">Tugboat Docs</span>
          </Link>
          <Link to={user ? "/dashboard" : "/"} data-testid="docs-back-link" className="flex items-center gap-2 text-sm font-700 hover:text-orange transition-colors">
            <ArrowLeft className="h-4 w-4" /> {user ? "Back to dashboard" : "Back home"}
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="lg:sticky lg:top-10 self-start">
          <p className="label-overline mb-3">Documentation</p>
          <nav className="space-y-1.5" data-testid="docs-nav">
            {TOPICS.map((t) => (
              <button key={t.id} onClick={() => setActive(t.id)} data-testid={`docs-nav-${t.id}`}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-700 border-2 transition-all text-left ${
                  active === t.id ? "bg-white border-ink shadow-nb-sm" : "border-transparent text-ink/70 hover:bg-white/70 hover:text-ink"
                }`}>
                <t.icon className="h-4 w-4 shrink-0" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="nb-card bg-white p-7 lg:p-10 min-w-0" data-testid="docs-content">
          {CONTENT[active]}
          <div className="mt-10 pt-6 border-t-2 border-ink/10 flex items-center justify-between text-sm font-700">
            <span className="text-ink/50">Still stuck?</span>
            <Link to={user ? "/support" : "/contact"} className="text-orange hover:underline flex items-center gap-1.5">
              <LifeBuoy className="h-4 w-4" /> Contact support
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
}
